import React, { createContext, useContext, useState, useCallback } from 'react';
import { devportalApi } from '../utils/devportalApi';

const UserStoreContext = createContext({});

export const useUserStore = () => {
    const context = useContext(UserStoreContext);
    if (!context) {
        throw new Error('useUserStore must be used within UserStoreProvider');
    }
    return context;
};

export const UserStoreProvider = ({ children }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentInvoice, setCurrentInvoice] = useState(null);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await devportalApi.listInvoices();
            setInvoices(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch invoices');
        } finally {
            setLoading(false);
        }
    }, []);

    const createInvoice = async (invoiceData) => {
        setLoading(true);
        setError(null);
        try {
            const res = await devportalApi.createInvoice(invoiceData);
            const data = res?.invoice || res?.data || res;
            setInvoices(prev => [data, ...prev]);
            return { data, error: null };
        } catch (err) {
            setError(err.message || 'Failed to create invoice');
            return { data: null, error: err };
        } finally {
            setLoading(false);
        }
    };

    const getInvoiceDetails = async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await devportalApi.getInvoiceDetails(id);
            const data = res?.invoice || res?.data || res;
            setCurrentInvoice(data);
            return { data, error: null };
        } catch (err) {
            setError(err.message || 'Failed to fetch invoice details');
            return { data: null, error: err };
        } finally {
            setLoading(false);
        }
    };

    const getInvoiceStatus = async (id) => {
        try {
            const data = await devportalApi.getInvoiceStatus(id);
            // Update the invoice in the list if it exists
            setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, status: data.status } : inv));
            if (currentInvoice && currentInvoice.id === id) {
                setCurrentInvoice(prev => ({ ...prev, status: data.status }));
            }
            return { data, error: null };
        } catch (err) {
            return { data: null, error: err };
        }
    };

    const payInvoice = async (id, options = {}) => {
        setLoading(true);
        setError(null);
        try {
            const res = await devportalApi.payInvoice(id, options);
            const data = res?.data || res;

            // If the API returns a redirect URL (e.g., Stripe Checkout)
            const checkoutUrl = res?.url || data?.url || res?.session_url || data?.session_url;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return { data: res, error: null };
            }

            // Update the invoice status in the list if it was a direct payment
            setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, status: 'paid', paid_at: new Date().toISOString() } : inv));
            if (currentInvoice && currentInvoice.id === id) {
                setCurrentInvoice(prev => ({ ...prev, status: 'paid', paid_at: new Date().toISOString() }));
            }
            return { data, error: null };
        } catch (err) {
            console.error('Payment processing failed:', {
                invoiceId: id,
                error: err,
                message: err.message
            });
            setError(err.message || 'Failed to process payment');
            return { data: null, error: err };
        } finally {
            setLoading(false);
        }
    };

    const value = {
        invoices,
        loading,
        error,
        currentInvoice,
        fetchInvoices,
        createInvoice,
        getInvoiceDetails,
        getInvoiceStatus,
        payInvoice
    };

    return (
        <UserStoreContext.Provider value={value}>
            {children}
        </UserStoreContext.Provider>
    );
};
